'use client';

import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from '../lib/theme';
import TopLoadingBar from '@/components/TopLoadingBar';
import { useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { UserProvider } from '@/contexts/UserContext';
import { ContextManagerProvider } from '@/contexts';
import PushNotificationProvider from '@/components/PushNotificationProvider';

import './globals.css';
import '../styles/tooltip.css';
import { Poppins } from 'next/font/google';

const poppins = Poppins({
  weight: ['400', '700'],
  style: ['normal', 'italic'],
  subsets: ['latin'],
  display: 'swap',
});

export default function RootLayout({ children }: { children: React.ReactNode }) {
  // Global network status hook
  useEffect(() => {
    function handleOffline() {
      toast.error("You are offline. Please check your internet connection.", { toastId: 'offline', autoClose: false });
    }
    function handleOnline() {
      toast.dismiss('offline');
      toast.success("You are back online!", { toastId: 'online', autoClose: 2000 });
    }
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  return (
    <html lang="en" className={poppins.className}>
      <body suppressHydrationWarning>
        <div className="app-layout-wrapper">
          <TopLoadingBar />
          <ThemeProvider theme={theme}>
            <UserProvider>
              <ContextManagerProvider>
                <PushNotificationProvider autoInitialize={true}>
                  {children}
                  <ToastContainer
                    position="top-right"
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="light"
                  />
                </PushNotificationProvider>
              </ContextManagerProvider>
            </UserProvider>
          </ThemeProvider>
        </div>
      </body>
    </html>
  );
}